/**
 * How many days a shipping rate takes in transit, as a range.
 *
 * Shippo's `estimated_days` is not enough to tell services apart: on a real
 * USPS quote Ground Advantage, Priority and Priority Express all came back as
 * 2, so the checkout's "Arrives" dates never moved when the buyer switched
 * method. The carrier's own wording in `duration_terms` does carry the spread
 * ("Delivery in 2 to 5 days.", "Delivery within 1, 2, or 3 days ...",
 * "Overnight delivery to most U.S. locations."), so it is read first and
 * `estimatedDays` is the fallback.
 */

export interface TransitDays {
  min: number;
  max: number;
}

interface RateTiming {
  estimatedDays?: number | string | null;
  durationTerms?: string | null;
}

/** Longer than any domestic or CJ route; past it the text is not a day count. */
const MAX_PLAUSIBLE_DAYS = 90;

function plausible(nums: number[]): TransitDays | null {
  const valid = nums.filter((n) => Number.isFinite(n) && n > 0 && n <= MAX_PLAUSIBLE_DAYS);
  if (valid.length === 0) return null;
  return { min: Math.min(...valid), max: Math.max(...valid) };
}

function fromTerms(terms: string): TransitDays | null {
  // "2 to 5 days", "7-21 business days", "7–12"
  const range = terms.match(/(\d+)\s*(?:-|–|to)\s*(\d+)/i);
  if (range) return plausible([Number(range[1]), Number(range[2])]);

  // "1, 2, or 3 days", "1 business day" -- only numbers that lead into "day",
  // so "by 10:30 AM next day" does not read as 10 to 30 days.
  const list = terms.match(/((?:\d+\s*,?\s*(?:or\s+|and\s+)?)+)(?:business\s+|calendar\s+)?days?\b/i);
  if (list) {
    // list[1] is one or more digit runs by the pattern above, so match() finds them.
    const found = plausible(list[1].match(/\d+/g).map(Number));
    if (found) return found;
  }

  if (/overnight|next[\s-](?:business\s+)?day/i.test(terms)) return { min: 1, max: 1 };
  return null;
}

/** The rate's transit range in days, or null when it says nothing usable. */
export function transitDays(rate: RateTiming): TransitDays | null {
  if (rate.durationTerms) {
    const found = fromTerms(rate.durationTerms);
    if (found) return found;
  }
  const days = Number(rate.estimatedDays);
  return Number.isFinite(days) && days > 0 ? { min: days, max: days } : null;
}

/** "1 day", "2-5 days" */
export function transitDaysLabel(range: TransitDays): string {
  if (range.min === range.max) return `${range.min} ${range.min === 1 ? 'day' : 'days'}`;
  return `${range.min}-${range.max} days`;
}

function shortDate(date: Date): string {
  return `${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getDate()}`;
}

/**
 * "Oct 6 - Oct 15": transit plus the seller's time to ship it, which is
 * allowed a day at best and a week at worst.
 */
export function deliveryDateRange(range: TransitDays, today: Date = new Date()): string {
  const first = new Date(today);
  first.setDate(today.getDate() + range.min + 1);
  const last = new Date(today);
  last.setDate(today.getDate() + range.max + 7);
  return `${shortDate(first)} - ${shortDate(last)}`;
}
